import { MediaTypeEnum } from '@ooh/contracts';
import type { TFunction } from 'i18next';

/**
 * Capitalizes the first letter of a string; returns '—' for null/empty input.
 * Shared fallback-label semantics for `buildMediaTypeOptions`, matching the
 * catalog's pre-existing `capitalize` helper (formerly duplicated per-feature —
 * see finding on PR #711).
 */
function capitalizeFallback(str: string | null): string {
  if (!str) return '\u2014';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Maps MediaTypeEnum values from a filter-options API to display options.
 * Labels come from the caller-provided localized map; unmapped values fall
 * back to a capitalized form of the raw enum value.
 *
 * Shared between the catalog (`features/media-units/lib/filter-fields.ts`) and
 * the offer units-tab (`features/offers/units-tab/lib/filter-fields.ts`) so the
 * two surfaces cannot silently diverge on media-type option display, mirroring
 * the existing `region-options.ts` sharing pattern.
 */
export function buildMediaTypeOptions(
  mediaTypes: string[],
  optionLabels: Record<string, string>,
): Array<{ value: string; label: string }> {
  return mediaTypes.map((mt) => ({ value: mt, label: optionLabels[mt] ?? capitalizeFallback(mt) }));
}

/**
 * Builds the localized media-type label map (MediaTypeEnum value -> translated
 * label) from the `map` namespace's `legend.types.*` keys. Shared between
 * `features/media-units/hooks/use-filter-labels.ts` and the offer units-tab
 * (`units-tab-page.tsx`) so both surfaces read the same translation-key
 * convention and cannot drift if it changes.
 */
export function buildMediaTypeOptionLabels(tMap: TFunction<'map'>): Record<string, string> {
  return Object.fromEntries(MediaTypeEnum.options.map((mt) => [mt, tMap(`legend.types.${mt}`)]));
}
